import {
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Fade,
    LinearProgress,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import PersonIcon from '@mui/icons-material/PersonOutlineOutlined';
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from '@mui/icons-material/Refresh';
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {getListingType, sortTypes} from "../services/Constants";
import ControlledTextField from "./ControlledTextField";
import ClearIcon from '@mui/icons-material/Clear';
import PersonIconFilled from '@mui/icons-material/Person';
import {MobileTooltip} from "./Tooltip";
import DownloadingIcon from '@mui/icons-material/Downloading';
let refreshTimer;
const MenuBar = React.memo(MenuBarBase);
function MenuBarBase({
                                    editing,
                                    setEditing,
                                    keywords,
                                    searchParams,
                                    setKeywords,
                                    disableProfileButton,
                                    user,
                                    handleToggle,
                                    refreshClick,
                                    sort,
                                    type,
                                    open,
                                    setValue,
                                    disabledSearch,
                                    showSpinner,
                                    loadingUser,
                                    loading,
                                    animationComplete,
                                    setAnimationComplete,
                                    handleTransitionEnd,
                                    openMessage,
                                    hasUpdate
                                }) {
    const theme = useTheme();
    const hiddenRef = useRef(null)
    const ref = useRef(null)
    const searchParamKeyword = searchParams.get("keywords");
    const hasSearchParams = searchParams?.toString() !== "";
    const [disableRefresh, setDisableRefresh] = useState(false);
    const handleSearch = () => {
        if (editing && keywords !== '' && searchParamKeyword === keywords && disabledSearch) {
            setKeywords('');
            ref?.current?.focus();
            return;
        }
        if (editing) {
            window.dispatchEvent(new Event('search'))
            return;
        }
        if (!disableRefresh) {
            clearTimeout(refreshTimer);
            setDisableRefresh(true);
            refreshTimer = setTimeout(() => {
                setDisableRefresh(false);
            }, 2000);
        }
        refreshClick();
    }

    useEffect(() => {
        clearTimeout(refreshTimer);
        setDisableRefresh(false);
    }, [editing]);

    useLayoutEffect(() => {
        setKeywords(searchParamKeyword);
    }, [searchParams])

    const handleOpen = useCallback(() => {
        if (keywords || keywords === '') handleToggle(1, null, true)
        if (!editing) {
            setAnimationComplete("running");
            setEditing(true);
        }
    }, [editing, keywords, handleToggle])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (open && editing && !disabledSearch) {
                if (event.key === "Enter" && !event.target?.classList?.contains("MuiSelect-select") && !event.target?.classList?.contains("MuiMenuItem-root")) {
                    window.dispatchEvent(new Event('search'));
                    return;
                }
            }
            if (open) return;
            if (event.key === '/' || event.key === '?') {
                setValue(0);
                handleToggle(0);
            }
            if (event.key === 's') {
                if (!ref.current) return;
                event.preventDefault();
                ref.current.focus();
                handleOpen();
            }
            if (event.key === 'r') {
                if (hasSearchParams) refreshClick();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleToggle, open, ref, refreshClick, handleOpen, editing, disabledSearch, hasSearchParams]);

    useEffect(() => {
        if (!ref.current) return;
        if (!editing) {
            ref.current.blur();
            setKeywords(searchParamKeyword ?? "")
        }
    }, [ref, editing, searchParams])

    const profileIconProps = {
        sx: {
            color: disableProfileButton ? 'rgba(236, 64, 122, 0.45)' : editing ? 'rgba(236, 64, 122, 0)' : undefined,
            transition: `color 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, width ${animationComplete === "start" ? "0.25s" : ""} ease-in-out 0.25s`,
            width: editing ? "0px" : "64px"
        }
    }
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const [shouldDelay, setShouldDelay] = useState(false);

    useEffect(() => {
        const element = hiddenRef.current;
        if (!element) return;
        const resizeObserver = new ResizeObserver(entries => {
            setShouldDelay(entries[0].contentRect.width >= (windowWidth - 180 > 372 ? 372 : windowWidth - 180) + 1);
        });
        resizeObserver.observe(element);
        return () => {
            resizeObserver.unobserve(element);
        };
    }, [hiddenRef.current, windowWidth])

    const maxWidth = windowWidth > 550 ? (editing ? 452 : 400) : windowWidth - (!editing ? 152 : 98)
    return (<>
            <div style={{overflow: "hidden", maxWidth: "0px", position: "relative", visibility: "hidden"}}>
        <span ref={hiddenRef} style={{
            fontSize: "1rem", position: 'absolute', whiteSpace: "nowrap", visibility: "hidden"
        }}>{hasSearchParams ? keywords !== '' ? keywords : "No keywords" : "Search here"}</span>
            </div>
            <Fade in={true}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="100vw"
                    sx={{
                        pointerEvents: "none",
                        zIndex: 100,
                        position: "fixed",
                        bottom: 0,
                    }}
                    className={`MenuBar`}
                >
                    <ButtonGroup
                        sx={{
                            pointerEvents: 'all',
                            height: '4em',
                            maxWidth: "526px",
                            minWidth: 0,
                            margin: "0 12px",
                            color: (theme) => theme.palette.primary.main,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            border: (theme) => `2px solid ${theme.palette.primary.main}`,
                            borderRadius: "12px",
                            '& .MuiButton-root': {
                                color: (theme) => theme.palette.primary.main,
                                backgroundColor: "transparent",
                                '&:hover': {
                                    backgroundColor: "rgba(236, 64, 122, 0.08)"
                                }
                            },
                            '--opac': shouldDelay && !editing ? '1' : '0',
                            '--animate': animationComplete === 'complete' ? "0s" : "0.25s",
                            '--delayAnimate': animationComplete === 'complete' || editing ? "0s" : "0.25s"
                        }}
                        variant="contained"
                        aria-label="outlined button group">
                        <MobileTooltip enterDelay={500} title={"Profile (/)"}>
                            <Button className={`MinWidthButton left ${editing ? "EditButton" : ""}`}
                                    tabIndex={editing ? -1 : undefined} sx={{
                                marginRight: "-1px",
                                borderRadius: '12px',
                                borderBottomLeftRadius: '12px',
                                pointerEvents: disableProfileButton || editing ? "none" : undefined,
                                transition: `background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, padding ${animationComplete === "start" ? "0.25s" : ""} ease-in-out 0.25s, min-width ${animationComplete === "start" ? "0.25s" : ""} ease-in-out 0.25s`,
                            }} onClick={() => {
                                handleToggle(0, null, true);
                            }}>
                                {!user ? <PersonIcon {...profileIconProps} /> :
                                    <PersonIconFilled {...profileIconProps} />}
                                {hasUpdate && <DownloadingIcon {...profileIconProps} style={{position: "absolute", top: "8px", right: "8px", width: editing ? "0px" : "20px"}} fontSize={"small"}/>}
                            </Button>
                        </MobileTooltip>
                        <MobileTooltip enterDelay={500} title={open ? "" : "Search options (s)"}>
                            <Stack className={"MenuStack"} direction={'column'} alignItems={"flex-start"}
                                   justifyContent={"center"} sx={{
                                width: "1000px",
                                backgroundColor: "transparent"
                            }}>
                                <ControlledTextField transitionEnd={handleTransitionEnd} handleClick={handleOpen}
                                                     placeholder={searchParamKeyword === null ? "Search here" : "No keywords"}
                                                     inputNewRef={ref} handleInput={handleOpen}
                                                     renderEndAdornment={false}
                                                     className={`FadeInput ${animationComplete === "complete" && editing ? "MenuInputEdit" : "MenuInput"}`}
                                                     margin='dense' value={keywords}
                                                     setValue={setKeywords} textFieldSx={{
                                    width: animationComplete !== "complete" ? hiddenRef.current?.offsetWidth + 28 : "100%",
                                    maxWidth: maxWidth,
                                    transition: animationComplete === "start" ? `max-width ${shouldDelay ? ".25s" : ""} ease-in-out 0.25s` : `width ease-in-out 0.25s, max-width ease-in-out ${animationComplete === 'complete' ? "0s" : "0.25s"}`
                                }} sx={{
                                    padding: 0,
                                    border: "none",
                                    fontSize: "1rem",
                                    height: !editing ? "24px" : "64px",
                                    transition: `${animationComplete === "start" ? ".25s" : ""} height ease-in-out 0.25s`
                                }}/>
                                {searchParams.get("keywords") !== null &&
                                    <Typography
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpen();
                                        }}
                                        color={'primary'}
                                        sx={{
                                            zIndex: 2,
                                            cursor: "pointer",
                                            textTransform: 'none',
                                            fontSize: "0.8rem",
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            width: "100%",
                                            textAlign: "center",
                                            maxWidth: "calc(100vw - 152px)",
                                            opacity: editing ? 0 : undefined,
                                            transition: `opacity ${animationComplete === "start" ? "0.25s" : ""} ease-in-out 0.25s`,
                                        }}>{sort && sortTypes[sort]} {type[0] !== '' && ('• ' + getListingType(type))}
                                    </Typography>
                                }
                            </Stack>
                        </MobileTooltip>
                        <MobileTooltip enterDelay={500}
                                       title={editing ? keywords !== '' && searchParamKeyword === keywords && disabledSearch ? "Clear search" : "Search (enter)" : "Refresh (r)"}>
                            <Button className={"MinWidthButton right"} sx={{
                                marginLeft: "-1px",
                                borderRadius: '12px',
                                borderBottomRightRadius: '12px',
                                pointerEvents: ((disabledSearch && editing && searchParamKeyword !== keywords) || (!editing && !hasSearchParams)) ? "none" : undefined
                            }} onClick={handleSearch}>
                                {editing ?
                                    showSpinner ? <CircularProgress size={16} thickness={6} sx={{
                                            color: "rgba(236, 64, 122, 0.5)",
                                            width: "64px"
                                        }}/> :
                                        keywords !== '' && searchParamKeyword === keywords && disabledSearch ?
                                            <ClearIcon sx={{width: "64px"}}/> :
                                            <SearchIcon sx={{
                                                color: disabledSearch ? 'rgba(236, 64, 122, 0.45)' : undefined,
                                                transition: "color 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                                                width: "64px"
                                            }}/>
                                    :
                                    <RefreshIcon sx={{
                                        color: !hasSearchParams ? 'rgba(236, 64, 122, 0.45)' : undefined,
                                        transition: "color 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                                        width: "64px",
                                        animation: disableRefresh ? "App-logo-spin ease-out 2s infinite" : undefined,
                                    }}/>}</Button>
                        </MobileTooltip>
                    </ButtonGroup>
                    <Fade unmountOnExit in={loading || loadingUser}>
                        <Box
                            className={`MenuBarLoad`}
                            width="100%"
                            sx={{
                                zIndex: 100,
                                position: "fixed",
                                bottom: 0,
                                maxWidth: "550px",
                            }}>
                            <LinearProgress className={"MenuBarLoadBar"} sx={{
                                margin: "0 76px", backgroundColor: 'transparent',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: 'rgba(236, 64, 122, 0.6)',
                                },
                            }}/>
                        </Box>
                    </Fade>
                </Box>
            </Fade>

        </>
    );

}
export default MenuBar;
